// src/components/e-commerce/product-details/tabs/reviews/review-media-uploader.tsx
"use client"

import { Upload } from "lucide-react"
import { useRef, useState } from "react"
import {
  ALLOWED_IMAGE_TYPES,
  ALLOWED_VIDEO_TYPES,
  fetchUploadNonce,
  MAX_MEDIA_COUNT,
  type MediaAttachment,
  uploadMediaToWordPress,
  validateMediaFile,
} from "@/services/media-upload-service"
import { MediaPreviewItem } from "./media-preview-item"

interface ReviewMediaUploaderProps {
  productId: number
  sessionCookie: string
  attachments: MediaAttachment[]
  onAttachmentsChange: (attachments: MediaAttachment[]) => void
  disabled: boolean
}

export function ReviewMediaUploader({
  productId,
  sessionCookie,
  attachments,
  onAttachmentsChange,
  disabled,
}: ReviewMediaUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const file = files[0]
    setUploadError(null)

    // Check session cookie
    if (!sessionCookie || sessionCookie.trim() === "") {
      setUploadError("Você precisa estar conectado para enviar mídia")
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    // Validate file
    const validation = validateMediaFile(file)
    if (!validation.valid) {
      setUploadError(validation.error?.message || "Invalid file")
      if (fileInputRef.current) fileInputRef.current.value = ""
      return
    }

    // Upload to WordPress
    setIsUploading(true)
    console.log(
      "[Media Uploader] Starting upload for file:",
      file.name,
      file.type,
      file.size
    )
    try {
      console.log("[Media Uploader] Fetching nonce...")
      const nonce = await fetchUploadNonce(productId, sessionCookie)
      console.log("[Media Uploader] Got nonce, uploading to WordPress...")
      const attachment = await uploadMediaToWordPress(
        file,
        productId,
        nonce,
        sessionCookie
      )
      console.log("[Media Uploader] Upload successful, attachment:", attachment)
      onAttachmentsChange([...attachments, attachment])
      console.log(
        "[Media Uploader] Attachments updated, new count:",
        attachments.length + 1
      )
    } catch (error) {
      console.error("[Media Uploader] Upload failed:", error)
      setUploadError(error instanceof Error ? error.message : "Upload failed")
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const handleRemove = (index: number) => {
    onAttachmentsChange(attachments.filter((_, i) => i !== index))
  }

  const isMaxReached = attachments.length >= MAX_MEDIA_COUNT
  const isDisabled = disabled || isUploading

  return (
    <div className='space-y-2'>
      <div className='flex flex-wrap gap-2'>
        {attachments.map((attachment, index) => (
          <MediaPreviewItem
            attachment={attachment}
            disabled={isDisabled}
            key={`${attachment.id}-${attachment.key}`}
            onRemove={() => handleRemove(index)}
          />
        ))}

        {!isMaxReached && (
          <label className='flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-gray-300 border-dashed transition-colors hover:border-gray-400'>
            <Upload className='mb-1 h-5 w-5 text-gray-400' />
            <span className='px-1 text-center text-gray-500 text-xs'>
              Add photos or videos
            </span>
            <input
              accept={[...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES].join(
                ","
              )}
              className='hidden'
              disabled={isDisabled}
              onChange={handleFileSelect}
              ref={fileInputRef}
              type='file'
            />
          </label>
        )}
      </div>

      {uploadError && <p className='text-red-600 text-sm'>{uploadError}</p>}

      {isUploading && <p className='text-gray-600 text-sm'>Uploading...</p>}

      <p className='text-gray-500 text-xs'>
        Maximum {MAX_MEDIA_COUNT} files. Images up to 5MB, videos up to 50MB.
      </p>
    </div>
  )
}
